import * as damConnectionStatusTypes from '../../consts/dam/damConnectionStatusTypes';
import damStatusTypes from '../../consts/dam/damStatusTypes';
import deviceSticksTypes from '../../consts/device/deviceSticksTypes';
import badgeTypes from '../../consts/loyalty/badgeTypes';
import userSegmentTypes from '../../consts/loyalty/userSegmentTypes';
import * as uamConnectionStatusTypes from '../../consts/uam/uamConnectionStatusTypes';
import * as uamWebSocketEventTypes from '../../consts/uam/uamWebSocketEventTypes';
import usageProfiles from '../../consts/usage/usageProfiles';
import BADGES_CONFIG from '../../containers/ClubPage/configs/badgesConfig';
import {selectIsRetail} from '../../state/selectors/global';
import {getState} from '../../state/store';
import objectUtil from '../../utils/objectUtil';
import valueUtils from '../../utils/valueUtils';
import deviceHelpers from '../device/deviceHelpers';
import * as loyaltyHelpers from '../loyalty/loyaltyHelpers';
import loyaltyRulesService, {calcDaysLeft, toMs} from '../loyalty/loyaltyRulesService';

const normalizeConnectionStatus = (status) =>
    status === uamConnectionStatusTypes.CONNECTED
        ? damConnectionStatusTypes.CONNECTED
        : damConnectionStatusTypes.DISCONNECTED;

const deviceConnectionStatusMapping = (response) => normalizeConnectionStatus(response?.status);

const subscriptionMapping = (response) => ({
    url: response?.url,
});

const getBatteryChargedVibrationFields = (data) =>
    objectUtil.filterUndefined({
        batteryChargedOneExp: data?.uiBatteryChargedVibration?.batteryChargedOneExp,
        batteryChargedTwoExp: data?.uiBatteryChargedVibration?.batteryChargedTwoExp,
    });

const getHeatingVibrationFields = (data) =>
    objectUtil.filterUndefined({
        fullyHeated: data?.heatingVibration?.fullyHeated,
        endOfHeating: data?.heatingVibration?.endOfHeating,
        nearEnd: data?.heatingVibration?.nearEnd,
        startHeating: data?.heatingVibration?.startHeating,
    });

const getDeviceSettingFields = (data) =>
    objectUtil.filterUndefined({
        customUI: data?.customUI?.colourScheme,
        brightness: data?.brightness?.value,
        flexBattery: data?.flexBattery?.mode,
        flexPuff: data?.flexPuff?.status,
        pauseMode: data?.pauseMode?.status,
        parentProtection: data?.parentProtection?.protectionType,
        autoStartHeating: data?.autoStartHeating?.featureState,
        liftUpGesture: data?.liftUpGesture?.gestureState,
        doubleTapGesture: data?.doubleTapGesture?.gestureState,
        ledIntensity: data?.ledIntensity?.intensity,
        uiBatteryChargedVibration: getBatteryChargedVibrationFields(data),
        heatingVibration: getHeatingVibrationFields(data),
    });

const getDeviceFields = (data) =>
    objectUtil.filterUndefined({
        batteryCharge: deviceHelpers.deviceBatteryChargeValueValidation(+data?.batteryInfo?.batteryCharge),
        codentify: data?.codentify,
        firmwareVersion: data?.firmwareVersion,
        batteryToSticks: data?.batteryInfo?.batteryToSticks,
        lastCharger: data?.last_charger,
        settings: getDeviceSettingFields(data),
    });

const getFirmwareUpdateFields = (data) =>
    objectUtil.filterUndefined({
        status: data?.status === damStatusTypes.RUNNING ? damStatusTypes.RUNNING : undefined,
        requestId: data?.requestId,
        progress: valueUtils.parseToNumber(data?.progress?.phaseProgress),
    });

const deviceInfoMapping = (response) => ({
    serialNumber: response?.serial_number,
    synchronizeUpdateDate: response?.synchronize_update_ts,
    connectionType: response?.communication_interface,
    modelType: response?.model_type,
    lastChargerDusn: response?.last_charger?.serialNumber,
    firmware: getFirmwareUpdateFields(response?.firmware_update_state),
    ...getDeviceFields(response?.device),
});

const registrationMapping = (response) => ({
    device: {
        dusn: response?.data?.dusn,
        assetId: response?.data?.asset_id,
        codentify: response?.data?.codentify,
    },
    holder: {
        dusn: response?.data?.holder_data?.dusn,
        assetId: response?.data?.holder_data?.asset_id,
        codentify: response?.data?.holder_data?.codentify,
    },
});

const userConsentsMapping = (response) => response?.data?.consents;

const userMessagesMapping = (response) => response?.data;

const experienceInferenceMapping = (response) => {
    const responseData = response?.data?.[0];

    if (!responseData) return {};

    const {inferredOn, result} = responseData || {};

    const {n_sticks_identified, min_n_experiences_required} = result?.calibration || {};

    if (n_sticks_identified < min_n_experiences_required) {
        return {
            inferenceTimestamp: inferredOn,
            calibration: result?.calibration,
            experienceScore: null,
            tipsToConsider: null,
            insightsAndTips: null,
            rawItems: response.data,
        };
    }

    const {tips_to_consider, ...experienceScoreData} = result?.experience_score || {};

    return {
        inferenceTimestamp: inferredOn,
        calibration: result?.calibration,
        experienceScore: experienceScoreData,
        tipsToConsider: tips_to_consider,
        insightsAndTips: {...result?.insights, ...result?.tips_and_tricks},
        rawItems: response.data,
    };
};

const experienceDataMapping = (response) => {
    // NOTE: ignore usage data for retail admin
    const isRetail = selectIsRetail(getState());
    const data = response?.data;
    const {perProfileStats, records, from, to} = data || {};

    const usageProfileMapping = (profileType) => {
        const profileData = perProfileStats?.[profileType];

        return {
            records: records?.map((record) => ({
                fromDate: record?.from,
                count: isRetail ? 0 : record?.[profileType]?.withinDeltaCount || 0,
            })),
            averagePuffsCount: profileData?.averagePuffsPerUsage || 0,
            averagePausesCount: profileData?.pauseCount || 0,
            totalUsageCount: profileData?.usageCount || 0,
            averageDurationSeconds: profileData?.averageDurationPerStickInSeconds || 0,
        };
    };

    return {
        fromDate: from,
        toDate: to,
        [deviceSticksTypes.TEREA]: usageProfileMapping(usageProfiles.TEREA_PROFILE),
        [deviceSticksTypes.LEVIA]: usageProfileMapping(usageProfiles.LEVIA_PROFILE),
    };
};

// NOTE: Move to selector
const userBadgesMapping = ({data: {badges = []} = {}} = {}) => {
    const byType = Object.fromEntries(badges.map((b) => [b.badge, b]));

    let previousType = undefined;

    return Object.fromEntries(
        Object.entries(BADGES_CONFIG).map(([type, cfg]) => {
            let milestonesStatus = undefined;
            let levelCompletedTs = undefined;
            let levelsStatus = [];
            let completedLevels = [];
            let completedMilestones = [];
            let isRewardReceived = false;
            let isBadgeAchieved = false;

            const rules = loyaltyRulesService.checkBadgeUnlockRules(type);
            let isLocked = rules.isLocked;
            let lockedMilestoneIndex = undefined;
            let lockedMilestoneTime = undefined;
            let daysLeft = undefined;

            const prevBadgeType = type !== badgeTypes.QUEST_CHAMPION && previousType;

            previousType = type;

            const index = parseInt(type.split('_').pop(), 10);
            const level = `level${index}`;
            const prevLevel = `level${index - 1}`;
            const parentBadgeType = cfg?.isSubBadge ? loyaltyHelpers.getParentBadgeType(type) : undefined;

            if (cfg?.isSubBadge) {
                const parent = byType[parentBadgeType];
                const parentLevels = parent?.levels || {};
                const prevLevelData = parentLevels[prevLevel];
                const currentLevelData = parentLevels[level];

                const lockedLevelIndexNum = parseInt(
                    String(parent?.nextLevel || '')
                        .split('level')
                        .pop(),
                    10
                );
                const nextLevelTsMs = toMs(parent?.nextLevelTimestamp);

                if (!Number.isNaN(lockedLevelIndexNum) && nextLevelTsMs && !isLocked) {
                    const timeNotArrived = nextLevelTsMs > Date.now();
                    const thisLevelOrAfter = lockedLevelIndexNum <= index;

                    isLocked = thisLevelOrAfter && timeNotArrived;
                }

                if (!isLocked) {
                    const nextMilestoneRaw = currentLevelData?.nextMilestone || '';
                    const idx = parseInt(nextMilestoneRaw.split('milestone').pop(), 10);

                    lockedMilestoneIndex = Number.isNaN(idx) ? undefined : idx;
                } else {
                    lockedMilestoneIndex = 0;
                }

                lockedMilestoneTime = currentLevelData?.nextMilestoneTimestamp;

                if (index === lockedLevelIndexNum && prevLevelData?.isCompleted) {
                    daysLeft = calcDaysLeft(parent?.nextLevelTimestamp);
                }

                levelCompletedTs = currentLevelData?.completedTs;
                milestonesStatus = currentLevelData?.milestones ?? {};
                isRewardReceived = Boolean(currentLevelData?.isRewardReceived);
            } else if (cfg?.levels) {
                const badgeData = byType[type];

                if (badgeData?.levels) {
                    completedLevels = Object.entries(badgeData.levels)
                        .filter(([, lvl]) => {
                            const m = lvl?.milestones;

                            return m && Object.values(m).every((x) => x?.isCompleted);
                        })
                        .map(([lvlName]) => lvlName);
                }

                levelsStatus = completedLevels;
                isRewardReceived = Boolean(badgeData?.isRewardReceived);
                isBadgeAchieved = Boolean(badgeData?.isBadgeAchieved);
            } else {
                const badgeData = byType[type];

                milestonesStatus = badgeData?.levels?.level1?.milestones ?? {};
                levelCompletedTs = badgeData?.badgeAchievedTs;
                isRewardReceived = Boolean(badgeData?.isRewardReceived);
                isBadgeAchieved = Boolean(badgeData?.isBadgeAchieved);

                if (type === badgeTypes.QUEST_CHAMPION) {
                    daysLeft = rules?.daysLeft;
                }
            }

            if (cfg.milestones) {
                completedMilestones = cfg.milestones.filter((_, i) => {
                    const key = `milestone${i + 1}`;

                    return milestonesStatus?.[key]?.isCompleted;
                });
            }

            const progress = cfg.levels ? completedLevels.length : completedMilestones.length;
            const total = cfg.levels ? cfg.levels.length : cfg.milestones?.length || 0;
            const isCompleted = total > 0 && progress === total;

            return [
                type,
                {
                    ...cfg,
                    badgeType: type,
                    parentBadgeType,
                    index,
                    level,
                    prevBadgeType,
                    milestonesStatus,
                    completedMilestones,
                    completedLevels,
                    levelsStatus,
                    totalMilestonesCount: total,
                    progress,
                    levelCompletedTs,
                    isLocked,
                    lockedMilestoneIndex,
                    lockedMilestoneTime,
                    daysLeft,
                    isCompleted,
                    isRewardReceived,
                    isBadgeAchieved,
                    isMultipleActions: total > 1,
                    lockingRule: rules.lockingRule,
                },
            ];
        })
    );
};

const userConversionStatusMapping = (response) => {
    const {isConversionable, userSegment, userSurveyResult, consecutive3days} = response?.data || {};

    const getConversionStatus = () => {
        switch (isConversionable) {
            case false:
                return damStatusTypes.FAILED;
            case true:
                return damStatusTypes.SUCCESS;
            default:
                return damStatusTypes.NOT_STARTED;
        }
    };

    return {
        conversionStatus: getConversionStatus(),
        userData: {
            userSegment: userSegment || userSegmentTypes.DEFAULT,
            sourcing: userSurveyResult?.sourcing,
            motivation: userSurveyResult?.motivation,
            reason: userSurveyResult?.reason,
            surveyResultTs: userSurveyResult?.surveyResultTs,
            isConsecutive3Days: !!consecutive3days?.status,
        },
    };
};

const silentFwuConfigMapping = (response) => {
    const {isSilentUpdateAllowed} = response?.data || {};

    return isSilentUpdateAllowed;
};

const retailDevicesMapping = (response) => response?.data;

const retailRewardsMapping = (response) => response?.data;

const requestStatusMapping = (response) => {
    return {
        eventType: response?.eventType || uamWebSocketEventTypes.ASYNC_REQUEST,
        requestId: response?.requestId,
        status: response?.status,
        request: valueUtils.safeJsonParse(response?.request),
        errorMessage: response?.errorMessage,
    };
};

export default {
    normalizeConnectionStatus,
    deviceConnectionStatusMapping,
    subscriptionMapping,
    deviceInfoMapping,
    getDeviceFields,
    registrationMapping,
    userConsentsMapping,
    userMessagesMapping,
    experienceInferenceMapping,
    experienceDataMapping,
    userBadgesMapping,
    userConversionStatusMapping,
    silentFwuConfigMapping,
    retailDevicesMapping,
    retailRewardsMapping,
    requestStatusMapping,
};
